// src/admin/AdminProfile.js
import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { updateEmail, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { auth, db } from '../firebase';
import { User, Mail, Phone, Lock, Save, Eye, EyeOff, Shield, AlertCircle } from 'lucide-react';

const AdminProfile = () => {
  const [profile, setProfile] = useState({ name: '', email: '', phone: '', role: '', idCard: '' });
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [profileMsg, setProfileMsg] = useState({ type: '', text: '' });
  const [passwordMsg, setPasswordMsg] = useState({ type: '', text: '' });

  useEffect(() => {
    const fetchProfile = async () => {
      const user = auth.currentUser;
      if (!user) return;
      try {
        const docRef = doc(db, 'users', user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setProfile({
            name: data.name || '',
            email: data.email || '',
            phone: data.phone || '',
            role: data.role || '',
            idCard: data.idCard || '',
          });
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setProfileMsg({ type: '', text: '' });
    setSavingProfile(true);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');

      await updateDoc(doc(db, 'users', user.uid), {
        name: profile.name,
        phone: profile.phone,
        idCard: profile.idCard,
      });

      if (profile.email !== user.email) {
        try {
          await updateEmail(user, profile.email);
          await updateDoc(doc(db, 'users', user.uid), { email: profile.email });
        } catch (emailErr) {
          console.error('Email update error:', emailErr);
          if (emailErr.code === 'auth/requires-recent-login') {
            setProfileMsg({
              type: 'error',
              text: 'Email change ke liye pehle logout kar ke dobara login karein.'
            });
            setSavingProfile(false);
            return;
          }
          throw emailErr;
        }
      }

      localStorage.setItem('adminName', profile.name);
      setProfileMsg({ type: 'success', text: 'Profile updated successfully!' });
      setTimeout(() => setProfileMsg({ type: '', text: '' }), 3000);

    } catch (error) {
      console.error('Profile update error:', error);
      setProfileMsg({ type: 'error', text: 'Failed to update profile. Try again.' });
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordMsg({ type: '', text: '' });

    if (!passwords.current || !passwords.new || !passwords.confirm) {
      setPasswordMsg({ type: 'error', text: 'All password fields are required.' });
      return;
    }

    if (passwords.new.length < 6) {
      setPasswordMsg({ type: 'error', text: 'New password must be at least 6 characters.' });
      return;
    }

    if (passwords.new !== passwords.confirm) {
      setPasswordMsg({ type: 'error', text: 'New password and confirm password do not match.' });
      return;
    }

    setSavingPassword(true);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');

      const credential = EmailAuthProvider.credential(user.email, passwords.current);
      await reauthenticateWithCredential(user, credential);

      await updatePassword(user, passwords.new);

      setPasswordMsg({ type: 'success', text: 'Password changed successfully!' });
      setPasswords({ current: '', new: '', confirm: '' });
      setTimeout(() => setPasswordMsg({ type: '', text: '' }), 3000);

    } catch (error) {
      console.error('Password change error:', error);
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        setPasswordMsg({ type: 'error', text: 'Current password is incorrect.' });
      } else {
        setPasswordMsg({ type: 'error', text: 'Failed to change password. Try again.' });
      }
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-green-700 border-t-transparent"></div>
        <p className="mt-4 text-gray-500">Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">

      <div>
        <h1 className="text-2xl font-bold text-gray-800">My Profile</h1>
        <p className="text-gray-500 text-sm mt-1">Manage your account settings</p>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
        <Shield className="text-green-700" size={24} />
        <div>
          <p className="text-sm font-semibold text-green-900">Your Role: <span className="capitalize">{profile.role}</span></p>
          <p className="text-xs text-green-700">Aap apni details aur password yahan se change kar sakte hain.</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-lg font-bold text-gray-800 mb-5 flex items-center gap-2">
          <User size={20} className="text-green-700" /> Personal Information
        </h2>

        {profileMsg.text && (
          <div className={`text-sm p-3 rounded-lg mb-4 ${
            profileMsg.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}>
            {profileMsg.text}
          </div>
        )}

        <form onSubmit={handleProfileUpdate} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name</label>
              <div className="relative">
                <User className="absolute left-3 top-3.5 text-gray-400" size={18} />
                <input
                  type="text"
                  value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 pl-11 focus:outline-none focus:border-green-600 text-sm"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-3.5 text-gray-400" size={18} />
                <input
                  type="email"
                  value={profile.email}
                  onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 pl-11 focus:outline-none focus:border-green-600 text-sm"
                  required
                />
              </div>
              <p className="text-xs text-yellow-600 mt-1 flex items-center gap-1">
                <AlertCircle size={12} /> Email change ke liye dobara login karna pad sakta hai.
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number</label>
              <div className="relative">
                <Phone className="absolute left-3 top-3.5 text-gray-400" size={18} />
                <input
                  type="tel"
                  value={profile.phone}
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 pl-11 focus:outline-none focus:border-green-600 text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">ID Card Number</label>
              <input
                type="text"
                value={profile.idCard}
                onChange={(e) => setProfile({ ...profile, idCard: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                placeholder="e.g., 35202-1234567-1"
              />
            </div>

          </div>

          <button
            type="submit"
            disabled={savingProfile}
            className={`bg-green-700 text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-800 transition flex items-center gap-2 ${
              savingProfile ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            <Save size={18} />
            {savingProfile ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-lg font-bold text-gray-800 mb-5 flex items-center gap-2">
          <Lock size={20} className="text-green-700" /> Change Password
        </h2>

        {passwordMsg.text && (
          <div className={`text-sm p-3 rounded-lg mb-4 ${
            passwordMsg.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}>
            {passwordMsg.text}
          </div>
        )}

        <form onSubmit={handlePasswordChange} className="space-y-4">

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Current Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-3.5 text-gray-400" size={18} />
              <input
                type={showPasswords.current ? 'text' : 'password'}
                value={passwords.current}
                onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-3 px-4 pl-11 pr-11 focus:outline-none focus:border-green-600 text-sm"
                placeholder="Apna current password daalein"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
              >
                {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">New Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-3.5 text-gray-400" size={18} />
              <input
                type={showPasswords.new ? 'text' : 'password'}
                value={passwords.new}
                onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-3 px-4 pl-11 pr-11 focus:outline-none focus:border-green-600 text-sm"
                placeholder="Naya password (min 6 characters)"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
              >
                {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Confirm New Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-3.5 text-gray-400" size={18} />
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                value={passwords.confirm}
                onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-3 px-4 pl-11 pr-11 focus:outline-none focus:border-green-600 text-sm"
                placeholder="Naya password dobara daalein"
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
              >
                {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={savingPassword}
            className={`bg-green-700 text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-800 transition flex items-center gap-2 ${
              savingPassword ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            <Lock size={18} />
            {savingPassword ? 'Changing...' : 'Change Password'}
          </button>

        </form>
      </div>

    </div>
  );
};

export default AdminProfile;